import { useId, useState, type DragEvent } from 'react';
import { Button, Icon } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import { contarArquivoCsv } from '../csv';
import type { CsvResumo } from '../formulario';

/**
 * Area de arrastar um CSV de contatos.
 *
 * O arquivo e lido e contado aqui, no navegador, e o unico dado que sobe
 * para o estado do formulario e o resumo (nome, total, invalidos). Na previa
 * nada e enviado; quando o backend existir, o upload real acontece so ao
 * iniciar o envio -- a contagem previa continua local.
 */
export function ImportarCsv({ csv, onCsv }: { readonly csv: CsvResumo | null; readonly onCsv: (resumo: CsvResumo | null) => void }): JSX.Element {
  const inputId = useId();
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [arrastando, setArrastando] = useState(false);

  const processar = async (arquivo: File | undefined): Promise<void> => {
    if (arquivo === undefined) {
      return;
    }
    setLendo(true);
    setErro(null);
    try {
      const contagem = await contarArquivoCsv(arquivo);
      onCsv({ fileName: arquivo.name, total: contagem.total, invalidos: contagem.invalidos });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível ler o arquivo.');
    } finally {
      setLendo(false);
    }
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>): void => {
    event.preventDefault();
    setArrastando(false);
    void processar(event.dataTransfer.files[0]);
  };

  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={inputId}
        onDragOver={(event) => {
          event.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-6 text-center transition-colors ${
          arrastando ? 'border-brand bg-brand/5' : 'border-edge bg-surface hover:border-brand/60'
        }`}
      >
        <Icon name="upload" size="lg" className="text-ink-subtle" />
        <span className="text-sm font-medium text-ink">Arraste o CSV aqui ou clique para escolher</span>
        <span className="text-xs text-ink-subtle">Uma linha por contato, com o telefone em qualquer coluna. Nada sobe: a contagem é feita neste navegador.</span>
        <input
          id={inputId}
          type="file"
          accept=".csv,text/csv,text/plain"
          className="sr-only"
          onChange={(event) => {
            void processar(event.target.files?.[0]);
            event.target.value = '';
          }}
        />
      </label>
      {lendo ? (
        <p role="status" className="text-xs text-ink-subtle">
          Lendo o arquivo…
        </p>
      ) : null}
      {erro === null ? null : (
        <p role="alert" className="text-xs text-status-critical">
          {erro}
        </p>
      )}
      {csv === null ? null : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-elevated px-3 py-2 text-sm">
          <span className="min-w-0 truncate font-medium text-ink">{csv.fileName}</span>
          <span className="tabular-nums text-ink-muted">
            {formatCount(csv.total)} {csv.total === 1 ? 'contato' : 'contatos'}, {formatCount(csv.invalidos)} {csv.invalidos === 1 ? 'inválido' : 'inválidos'}
          </span>
          <Button variant="ghost" size="sm" icon="x" onClick={() => onCsv(null)}>
            Remover
          </Button>
        </div>
      )}
    </div>
  );
}
